/**
 * EventPrizeBreakdownCard — contestant-facing prize display for an event.
 *
 * Shown only when the organizer enabled "display prize to contestants". Renders
 * the (live-computed) prize pool prominently plus the per-round / per-place
 * breakdown the organizer configured. For a dynamic event the total updates as
 * entries come in. Returns null when display is off or there's nothing to show.
 */

import { Trophy } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useEventPrize } from '@/api/extensions/usePrizeOps';
import { formatCurrency } from '@/utils/formatters';

export function EventPrizeBreakdownCard({
  eventId,
  displayEnabled,
  performances,
}: {
  eventId: string;
  displayEnabled: boolean;
  /** The rodeo's performances, so a round can be labelled with where it runs. */
  performances?: Array<{ id: string; performance_name?: string | null }>;
}) {
  const { data, isLoading } = useEventPrize(eventId, { enabled: displayEnabled });

  if (!displayEnabled) return null;

  const isDynamic = data?.allocation_mode === 'dynamic';
  const total = data?.calculation.total ?? 0;
  const rounds = data?.prize_rounds ?? [];
  const perfName = (id: string | null | undefined) =>
    id ? performances?.find((p) => p.id === id)?.performance_name ?? null : null;
  // Rounds are stored without a name unless the organizer typed one, and in a
  // finals rodeo each round runs in a different performance — say which.
  const roundLabel = (r: (typeof rounds)[number]) => {
    const name = r.round_name?.trim() || (r.is_average ? 'Average (All Rounds)' : `Round ${r.round_number}`);
    const perf = r.is_average ? null : perfName(r.performance_id);
    return perf ? `${name} — ${perf}` : name;
  };

  // A fixed event with no prize stays hidden; a dynamic event always shows
  // (its pool grows with entries, so $0-with-0-entries is still meaningful).
  if (!isLoading && total <= 0 && !isDynamic) return null;

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-amber-600 dark:text-amber-500">
              <Trophy className="h-4 w-4" />
              <span className="text-xs font-semibold uppercase tracking-wide">Prize Pool</span>
            </div>
            <div className="mt-1 text-3xl font-bold">
              {isLoading ? '…' : formatCurrency(total)}
            </div>
            {isDynamic && (
              <p className="text-xs text-muted-foreground mt-0.5">
                Updates live with entries{data ? ` · ${data.entry_count} entered` : ''}
              </p>
            )}
          </div>
        </div>

        {rounds.length > 0 && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {rounds.map((r) => (
              <div key={r.round_number} className="rounded-md border border-border p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold">{roundLabel(r)}</span>
                  <span className="text-sm font-semibold">{formatCurrency(r.total)}</span>
                </div>
                <div className="mt-2 space-y-1">
                  {r.places.map((p) => (
                    <div
                      key={p.place}
                      className="flex items-center justify-between text-sm text-muted-foreground"
                    >
                      <span>{ordinal(p.place)} Place</span>
                      <span className="font-medium text-foreground">{formatCurrency(p.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}
